import { useEffect, useState } from 'react'
import { Icon } from './Icon'
import type { IconName } from './Icon'
import { useQuickReference } from '../hooks/useQuickReference'
import { useResourceOverrides } from '../hooks/useResourceOverrides'
import type {
  EventResourceCategory,
  QuickRefActivity,
  QuickRefContact
} from '../types'
import { SafetyBriefingTab } from './live/quickref/SafetyBriefingTab'
import { EventPlanTab } from './live/quickref/EventPlanTab'
import { ActivitiesTab } from './live/quickref/ActivitiesTab'
import { StationMapsTab } from './live/quickref/StationMapsTab'
import { KeyContactsTab } from './live/quickref/KeyContactsTab'
import { FirstAidTab } from './live/quickref/FirstAidTab'

// ResourcesPanel — full-screen quick-reference document for an event.
// Rebuilt for spec 23 v2 (replaces the L1 link-list version that landed
// alongside migration 048). Each tab body is a self-contained operational
// page authored against prototype L3642-3788 — oxblood callouts, bulleted
// responsibilities, activity-card grids, station maps, on-duty contacts
// with tel: links, defibrillator locations.
//
// Interface unchanged from L1 so existing callers (LiveDayView header,
// EventDetail's ?openResources= deep-link from the Resources Directory)
// keep working without edits.
//
// Behaviour:
//   - Closes on Escape, backdrop click, and the ✕ button.
//   - Body scroll lock while open.
//   - Read-on-mount via useQuickReference (5 min staleTime). Loading +
//     error states surface inline rather than hiding the tab strip.
//
// Visual contract:
//   - Modal background #fdfbf6 (`paper` per prototype; not a Tailwind
//     token, so inline-styled here).
//   - Brass-pill tab strip — rounded chips, bg-brass-soft active,
//     bg-white inactive with line border.
//   - 920×85vh modal centred on a dark scrim.
//
// `EventResources` rows are no longer rendered in the panel body. The
// table stays in the DB (powering spec 46's Resources Directory grid);
// the auto-seed link-shortcuts are now obsolete here.

interface Props {
  eventId: number
  eventName: string
  onClose: () => void
  initialCategory?: EventResourceCategory
}

// Tab strip — keys must match the EventResources category slugs (public
// contract from L1; the ?openResources= deep-link depends on these).
interface TabSpec {
  key: EventResourceCategory
  label: string
  icon: IconName
}
const TABS: ReadonlyArray<TabSpec> = [
  { key: 'briefing',   label: 'Safety Briefing', icon: 'shield-check' },
  { key: 'plan',       label: 'Event Plan',      icon: 'file' },
  { key: 'activities', label: 'Activities',      icon: 'star' },
  { key: 'maps',       label: 'Station Maps',    icon: 'map' },
  { key: 'contacts',   label: 'Key Contacts',    icon: 'users' },
  { key: 'first-aid',  label: 'First Aid',       icon: 'first-aid' }
]

const TOAST_DURATION_MS = 3500

export function ResourcesPanel({ eventId, eventName, onClose, initialCategory }: Props) {
  const [tab, setTab] = useState<EventResourceCategory>(initialCategory ?? 'briefing')
  const [toast, setToast] = useState<string | null>(null)
  const { data, isLoading, error } = useQuickReference(eventId)
  const {
    resolveSafetyBriefing,
    resolveEventPlan,
    resolveFirstAid
  } = useResourceOverrides()

  // Override resolvers — pre-compute every tab's resolved content so the
  // switch-on-tab below doesn't need to re-derive on each render. The
  // Event Plan resolver takes the DB value from the live payload (NULL
  // until admin authoring lands; see migration 052).
  const briefingContent = resolveSafetyBriefing(eventId)
  const planContent = resolveEventPlan(
    eventId,
    data
      ? {
          operatingPattern: data.operatingPattern,
          todaysProgramme: data.todaysProgramme,
          visitorFlow: data.visitorFlow
        }
      : null
  )
  const firstAidContent = resolveFirstAid(eventId)

  // Escape to close. Hooks unconditional (§2) — no early returns before
  // this useEffect call.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  // Body scroll lock so the page underneath doesn't scroll on touch.
  useEffect(() => {
    const previous = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => { document.body.style.overflow = previous }
  }, [])

  // Auto-dismiss toast.
  useEffect(() => {
    if (!toast) return
    const id = window.setTimeout(() => setToast(null), TOAST_DURATION_MS)
    return () => window.clearTimeout(id)
  }, [toast])

  return (
    <div
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={`Quick reference for ${eventName}`}
      className="fixed inset-0 z-[400] flex items-center justify-center"
      style={{ background: 'rgba(0,0,0,0.7)', padding: 20 }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="text-ink rounded-xl shadow-2xl w-full flex flex-col overflow-hidden"
        style={{ background: '#fdfbf6', maxWidth: 920, height: '85vh' }}
      >
        {/* Header — single-line "Quick Reference — {event.name}" per
            prototype L3648. Truncates with ellipsis on narrow screens
            rather than wrapping; the event name is also rendered as the
            modal's aria-label so screen readers still get the full text. */}
        <div className="flex items-center justify-between gap-3 px-6 py-4 border-b border-line flex-shrink-0">
          <div className="font-display text-xl font-semibold leading-tight min-w-0 truncate">
            Quick Reference — {eventName}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="text-ink-soft hover:text-ink hover:bg-line/40 rounded p-1.5 transition-colors flex-shrink-0"
          >
            <Icon name="x" size={18} />
          </button>
        </div>

        {/* Brass-pill tab strip */}
        <div
          className="flex gap-2 px-6 py-3 border-b border-line overflow-x-auto flex-shrink-0"
          role="tablist"
          aria-label="Quick reference sections"
          style={{ background: '#fdfbf6' }}
        >
          {TABS.map((t) => {
            const active = tab === t.key
            return (
              <button
                key={t.key}
                role="tab"
                aria-selected={active}
                type="button"
                onClick={() => setTab(t.key)}
                className={
                  'inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full text-sm font-semibold border transition-colors whitespace-nowrap ' +
                  (active
                    ? 'bg-brass-soft border-brass text-brass'
                    : 'bg-white border-line text-ink-soft hover:border-brass/50 hover:text-ink')
                }
              >
                <Icon name={t.icon} size={14} />
                <span>{t.label}</span>
              </button>
            )
          })}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5 scrollbar-clean">
          {isLoading && (
            <div className="text-ink-soft text-sm font-mono">Loading quick reference…</div>
          )}
          {error && (
            <div className="bg-oxblood-soft border border-oxblood text-oxblood rounded p-3 text-sm">
              <div className="font-semibold mb-1">Could not load quick reference</div>
              <pre className="text-xs whitespace-pre-wrap">{(error as Error).message}</pre>
            </div>
          )}
          {!isLoading && !error && data && (
            <TabBody
              tab={tab}
              eventId={eventId}
              data={data}
              briefingContent={briefingContent}
              planContent={planContent}
              firstAidContent={firstAidContent}
              onToast={setToast}
            />
          )}
        </div>
      </div>

      {/* Toast (Tab 2's "Opening full event plan…" hint, etc.) */}
      {toast && (
        <div
          className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[450] bg-ink text-parchment text-sm font-semibold px-4 py-2.5 rounded-full shadow-2xl"
          role="status"
          aria-live="polite"
        >
          {toast}
        </div>
      )}
    </div>
  )
}

// Body switchboard kept inline — the sub-components are split for
// readability but compose trivially. Each tab takes only the slice of
// the payload (or resolved override content) that it cares about.
function TabBody({
  tab,
  eventId,
  data,
  briefingContent,
  planContent,
  firstAidContent,
  onToast
}: {
  tab: EventResourceCategory
  eventId: number
  data: {
    location: string | null
    operatingPattern: string | null
    todaysProgramme: string | null
    visitorFlow: string | null
    activities: QuickRefActivity[]
    contacts: QuickRefContact[]
  }
  briefingContent: import('../hooks/useResourceOverrides').SafetyBriefingContent
  planContent: import('../hooks/useResourceOverrides').EventPlanContent
  firstAidContent: import('../hooks/useResourceOverrides').FirstAidContent
  onToast: (m: string) => void
}) {
  switch (tab) {
    case 'briefing':
      return <SafetyBriefingTab content={briefingContent} />
    case 'plan':
      return (
        <EventPlanTab
          eventId={eventId}
          operatingPattern={data.operatingPattern}
          todaysProgramme={data.todaysProgramme}
          visitorFlow={data.visitorFlow}
          onToast={onToast}
          content={planContent}
        />
      )
    case 'activities':
      return <ActivitiesTab activities={data.activities} />
    case 'maps':
      return <StationMapsTab location={data.location} />
    case 'contacts':
      return <KeyContactsTab contacts={data.contacts} />
    case 'first-aid':
      return <FirstAidTab content={firstAidContent} />
  }
}
